
import { ApiService } from "src/app/services/api/api.service";
import { NotyfService } from "./../notyf/notyf.service";
import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { CartItem } from "src/app/interfaces/cart-item";

import { environment } from "src/environments/environment";
import moment from 'moment';
const CLEAR_CART_TIMEOUT = environment.settings.cart.CLEAR_CART_TIMEOUT * 60 * 60 * 1000;

@Injectable({
  providedIn: "root",
})
export class CartService {

  settings: any;
  accept_booking_at_current_slot: any ;
  private _cart: CartItem[] = [];
  private _oldCart: CartItem[] = [];
  private _overwriteCart: CartItem[] = [];
  private _oldCartSet: boolean = false;
  cart$: Observable<any>;
  private cartSubject: BehaviorSubject<any>;
  private expiredSubject = new BehaviorSubject<CartItem[]>([]);
  expired$ = this.expiredSubject.asObservable();
  private unavailableSubject = new BehaviorSubject<CartItem[]>([]);
  unavailable$ = this.unavailableSubject.asObservable(); 
  private cart_timeout = null;
  private loggedIn: boolean = false;
  private externalCartId: number = null;
  venue_accept_booking_at_current_slot: string = 'yes';
  private validatingCart = false;
  private lastValidation = 0;
  private validationTimeGap = 3000; // 3 seconds
  private notifiedInvalidItems = new Set<string>(
    JSON.parse(localStorage.getItem("notifiedInvalidItems") || "[]")
  );
  private notifiedInvalidItemsInternal = new Set<string>(
    JSON.parse(localStorage.getItem("notifiedInvalidItems") || "[]")
  );
  private cartLoading = false;
  private lastCartFetch = 0;

  constructor(private router: Router, private notyf: NotyfService, private api: ApiService) {
    this._cart = this.getCartFromStorage();
    this.cartSubject = new BehaviorSubject<any>(this._cart);
    this.cart$ = this.cartSubject.asObservable();   
  }

  async validateCartOnce() {
    const now = Date.now();  
    if (this.validatingCart) return;  
    if (now - this.lastValidation < this.validationTimeGap) return;  
    this.validatingCart = true;
    this.lastValidation = now;  
    try{
      await this.getCartExternal();
    }finally {
      this.validatingCart = false;
    }  
  }

  hasInvalidItems(): boolean {
    return this.invalidItemCount() > 0;
  }

  invalidItemCount(): number {
    return this._cart.filter(item => item.status !== 'valid').length;
  }
  
  validItems(): CartItem[] {
    return this._cart.filter(item => item.status === 'valid');
  }
  
  // removeInvalidItems() {
  //   this._cart = (this._cart || []).filter(item => item.status === 'valid');
  
  //   localStorage.setItem("cart", JSON.stringify(this._cart));
  //   this.updateCartTimestamp();
  //   this.cartSubject.next(this._cart);
  //   this.resetTimeout();
  // }

  async removeInvalidItems(isLoggedIn: boolean = false) {

    const cart  = this._cart ;
    const validItems = (this._cart || []).filter(item => item.status === 'valid');
  
    this._cart = validItems;
    localStorage.setItem("cart", JSON.stringify(this._cart));
    this.updateCartTimestamp();
    this.cartSubject.next(this._cart);
    this.resetTimeout();
  
    if (isLoggedIn) {
      this.removeCartExternally(cart);
    }
  }

  async removeCartExternally(cart: any) {
    if(cart){      
      const invalidItems = (cart || [])
        .filter(item => item.status != 'valid')
        .map(item => item.uuid);    
      if (invalidItems.length === 0) return;    
      try {
        await this.api.post('cart/remove-invalid-items', { uuids: invalidItems }).toPromise();
      } catch (error) {
        console.error('Failed to sync cart externally:', error);
      }
    }    
  }

  updateCartTimestamp() {
    localStorage.setItem("cart_last_activity", moment().toISOString());
  }

  isCartExpired(): boolean {
    const lastActivity = localStorage.getItem("cart_last_activity");  
    if (!lastActivity) return false;  
    const diff = moment().diff(moment(lastActivity));  
    return diff > CLEAR_CART_TIMEOUT;
  }

  // async validateCartBeforeCheckout(): Promise<boolean> {
  //   try {
  //     const cart = await this.getCartExternal();
  //     if (!cart || cart.length === 0) {
  //       this.notyf.error("Some bookings are no longer available.");
  //       return false;
  //     }
  //     return true;
  //   } catch {
  //     this.notyf.error("Unable to validate bookings.");
  //     return false;
  //   }
  // }

  async validateCartBeforeCheckout(): Promise<boolean> {
    try {
      const cart = await this.getCartExternal(); // fetch latest status from server
      if (!cart || cart.length === 0) {
        this.notyf.error("Your cart is empty or all bookings are no longer available.");
        return false;
      }
  
      const hasInvalid = cart.some(item => item.status !== "valid");
      if (hasInvalid) {
        const invalidItems = cart.filter(item => item.status !== "valid");
        invalidItems.forEach(item => {
          if (!item) return;
          const key = `${item.date} ${item.time}_${item.employee_id}_${item.service_id}_${item.status}`;  
          if (this.notifiedInvalidItems.has(key)) {
            return;
          }
          this.notifiedInvalidItems.add(key);
          localStorage.setItem("notifiedInvalidItems",JSON.stringify([...this.notifiedInvalidItems])); 
          const booking_date = moment(`${item.date} ${item.time}`, "YYYY-MM-DD HH:mm");
          const msg =
            item.status === "expired"
              ? `Your booking for ${item.service_name} at ${item.venue_name} on ${booking_date.format("ddd Do MMM")} at ${booking_date.format("h:mm A")} has expired.`
              : `Your booking for ${item.service_name} at ${item.venue_name} on ${booking_date.format("ddd Do MMM")} at ${booking_date.format("h:mm A")} is no longer available.`;
          this.notyf.error(msg);
        });
        return false; // abort checkout
      }
  
      return true; // all items valid
    } catch (error) {
      this.notyf.error("Unable to validate cart. Please try again.");
      return false;
    }
  }

  async getCartVenueSettings(){
   
  }

  addBooking(booking) {
    let cart = this._cart;
    if (cart.length === 0) {
      cart = this.getCartFromStorage();
    }
    cart.push(booking);    
    localStorage.setItem("cart", JSON.stringify(cart));
    this.updateCartTimestamp();
    this.cartSubject?.next(cart);
    this.resetTimeout();
    return cart;
  }

  addItemExternal(booking: CartItem) {
    console.log(booking);
    this.api.post("/cart/add-item", booking).subscribe((response) => this.handleExternalResponse(response));
  }

  private handleExternalResponse(response) {
    if (response["success"] === true) {
      if (typeof response["data"] !== "undefined") {
        this._cart = response["data"]["items"];
        this.cartSubject?.next(this._cart);
        localStorage.setItem("cart", JSON.stringify(this._cart));
        this.updateCartTimestamp();
        if (response["data"]["deleted_items"]) {
          response["data"]["deleted_items"].forEach((item: CartItem) => {
            const booking_date = moment(`${item.date} ${item.time}`, "YYYY-MM-DD HH:mm");
            this.notyf.open({
              type: "info",
              message: `Your booking for a ${item.service_name} at ${item.venue_name} on ${booking_date.format(
                "ddd Do MMM"
              )} at ${booking_date.format("H:mm A")} has expired. It has been removed from your cart.`,
              dismissible: true,
              duration: 1000 * 10, // 10 seconds
            });
          });
        }
      }
    }
  }

  private resetTimeout() {
    if (this.cart_timeout !== null) {
      clearTimeout(this.cart_timeout);
    }
    if (this.cartSubject.value.length > 0) {
      this.cart_timeout = setTimeout(() => {
        this.clearCart();
        this.notyf.open({
          type: "info",
          message: "Your cart has been emptied due to inactivity.",
          dismissible: true,
          duration: 0,
        });
      }, CLEAR_CART_TIMEOUT);
    }
  }

  public getCartFromStorage() {
    if (this.isCartExpired()) {  
      localStorage.removeItem("cart");
      localStorage.removeItem("cart_last_activity");  
      this.notyf.open({
        type: "info",
        message: "Your cart has expired due to inactivity.",
        dismissible: true,
        duration: 8000,
      });  
      return [];
    }  
    return JSON.parse(localStorage.getItem("cart")) || [];
  }
 
  // getCart() {

  //   let cart = [...this.cartSubject.getValue()];
  //   this.venue_accept_booking_at_current_slot = localStorage.getItem("accept_booking_at_current_slot");
  
  //   const now = moment();
  //   const validItems: CartItem[] = [];
  
  //   cart.forEach((item: CartItem) => {
  
  //     if (!item) return;
  
  //     const bookingDate = moment(`${item.date} ${item.time}`, "YYYY-MM-DD HH:mm");
  //     let expiryDate = bookingDate.clone();
  
  //     if (this.venue_accept_booking_at_current_slot === 'yes') {
  
  //       if (item.next_slot) {
  //         expiryDate = moment(`${item.date} ${item.next_slot}`, "YYYY-MM-DD HH:mm");
  //       } else {
  //         expiryDate = expiryDate.add(item.time_taken, 'minutes');
  //       }
  //     }
  
  //     if (expiryDate.isAfter(now)) {
  //       validItems.push(item);
  //     } else {
  
  //       this.notyf.open({
  //         type: "info",
  //         message: `Your booking for a ${item.service_name} at ${item.venue_name} on ${bookingDate.format("ddd Do MMM")} at ${bookingDate.format("H:mm A")} has expired.`,
  //         dismissible: true,
  //         duration: 10000,
  //       });
  
  //       this.removeItemExternalByItem(item); // safer than by index
  //     }
  //   });
  
  //   this._cart = validItems;
  //   localStorage.setItem("cart", JSON.stringify(validItems));
  //   this.cartSubject.next(validItems);
  //   this.updateCartTimestamp();
  //   this.resetTimeout();
  
  //   return validItems;    
  // }

  getCart() {
    let cart = [...this.cartSubject.getValue()];
    this.venue_accept_booking_at_current_slot = localStorage.getItem("accept_booking_at_current_slot");  
    const now = moment();
    const validItems: CartItem[] = [];  
    cart.forEach((item: CartItem) => {  
      if (!item) return;
      const key = `${item.date} ${item.time}_${item.employee_id}_${item.service_id}_${item.status}`;  
      if (this.notifiedInvalidItems.has(key)) {
        return;
      }
      this.notifiedInvalidItems.add(key); 
      localStorage.setItem("notifiedInvalidItems",JSON.stringify([...this.notifiedInvalidItems]));  
      const bookingDate = moment(`${item.date} ${item.time}`, "YYYY-MM-DD HH:mm");
      let expiryDate = bookingDate.clone();  
      if (this.venue_accept_booking_at_current_slot === 'yes'){  
        if (item.next_slot) {
          expiryDate = moment(`${item.date} ${item.next_slot}`, "YYYY-MM-DD HH:mm");
        } else {
          expiryDate = expiryDate.add(item.time_taken, 'minutes');
        }
      }  
      if(expiryDate.isAfter(now)) {
        validItems.push(item);
      }else{  
        this.notyf.open({
          type: "info",
          message: `Your booking for a ${item.service_name} at ${item.venue_name} on ${bookingDate.format("ddd Do MMM")} at ${bookingDate.format("H:mm A")} has expired.`,
          dismissible: true,
          duration: 10000,
        });        
      }
    });  
    this.updateCartTimestamp();
    this.resetTimeout();
    return cart
  }

  removeItemExternalByItem(item: CartItem) {
    this.api.post("/cart/remove-item", {
      service_id: item.service_id,
      date: item.date,
      time: item.time,
      employee_id: item.employee_id
    }).subscribe(response => this.handleExternalResponse(response));
  }

  // async getCartExternal() {        
  //   const now = Date.now();  
  //   // prevent multiple calls within 1 seconds
  //   if (now - this.lastCartFetch < 1000) {
  //     return this._cart;
  //   }  
  //   // prevent overlapping requests
  //   if (this.cartLoading) {
  //     return this._cart;
  //   }  
  //   this.cartLoading = true;
  //   this.lastCartFetch = now;  
  //   try {  
  //     const response = await this.api.get("/cart").toPromise(); 
  //     if (response["success"] === true) {  
  //       const data = response["data"];
  //       const cart = data["items"] || [];        
  //       localStorage.setItem(
  //         "accept_booking_at_current_slot",
  //         data["accept_booking_at_current_slot"]
  //       );  
  //       const expired = cart.filter(item => item.status === "expired");
  //       const unavailable = cart.filter(item => item.status === "unavailable");  
  //       this.expiredSubject.next(expired);
  //       this.unavailableSubject.next(unavailable);  
  //       const invalidItems = [...expired, ...unavailable];          
  //       invalidItems.forEach((item: CartItem) => {  
  //         const key = `${item.date} ${item.time}_${item.employee_id}_${item.service_id}_${item.status}`;
  //         if(this.notifiedInvalidItems.has(key)){
  //           return;
  //         }  
  //         this.notifiedInvalidItems.add(key);  
  //         localStorage.setItem("notifiedInvalidItems",JSON.stringify([...this.notifiedInvalidItems]));  
  //         const booking_date = moment(
  //           `${item.date} ${item.time}`,
  //           "YYYY-MM-DD HH:mm"
  //         );  
  //         const type = item.status === "expired" ? "info" : "warning";  
  //         const message = item.status === "expired" ? "has expired" : "is no longer available";  
  //         this.notyf.open({
  //           type: type,
  //           message: `The selected time slot for ${item.service_name} at ${item.venue_name} on ${booking_date.format(
  //             "ddd Do MMM"
  //           )} at ${booking_date.format("h:mm A")} ${message}.`,
  //           dismissible: true,
  //           duration: 10000
  //         });  
  //       });  
  //       this._cart = cart;  
  //       this.cartSubject?.next(this._cart);  
  //       localStorage.setItem("cart", JSON.stringify(this._cart));  
  //       this.updateCartTimestamp();  
  //       return cart;  
  //     }  
  //     return false;  
  //   }finally{  
  //     this.cartLoading = false;  
  //   }  
  // }

  async getCartExternal() {
    const response = await this.api.get("/cart").toPromise();
    if (response["success"] === true) {
      const cart = response["data"]["items"];
      localStorage.setItem("accept_booking_at_current_slot", response["data"]["accept_booking_at_current_slot"]);
      if (response["data"]["deleted_items"]) {
        response["data"]["deleted_items"].forEach((item: CartItem) => {
          const booking_date = moment(`${item.date} ${item.time}`, "YYYY-MM-DD HH:mm");
          this.notyf.open({
            type: "info",
            message: `Your booking for a ${item.service_name} at ${item.venue_name} on ${booking_date.format(
              "ddd Do MMM"
            )} at ${booking_date.format("H:mm A")} has expired. It has been removed from your cart.`,
            dismissible: true,
            duration: 1000 * 10, // 10 seconds
          });
        });
      }
      this._cart = cart;
      this.cartSubject?.next(this._cart);
      localStorage.setItem("cart", JSON.stringify(this._cart));
      this.updateCartTimestamp();
      return cart;
    }
    return false;
  }

  saveCartExternal() {
    this.api.post("/cart/save-cart", { items: this.getCart() }).subscribe((response) => this.handleExternalResponse(response));
  }

  overwriteCart(cart: CartItem[]) {
    if (this._cart.length > 0 && this._oldCartSet === false) {
      this._oldCart = this._cart;
      this._oldCartSet = true;
    }
    if (this._overwriteCart !== cart) {
      this._cart = cart;
      this._overwriteCart = cart;
      localStorage.setItem("cart", JSON.stringify(this._cart));
      this.updateCartTimestamp();
      this.cartSubject?.next(this._cart);
    }
    this.resetTimeout();
  }

  removeItem(index: number, uuid?: string) {
    const cart = this.cartSubject?.getValue();
    if (cart[index]) {
      cart.splice(index, 1);
    }
    localStorage.setItem("cart", JSON.stringify(cart));
    this.updateCartTimestamp();
    this.cartSubject?.next(cart);
    this.resetTimeout();
    return cart;
  }

  removeItemExternal(index: number, uuid?: string) {
    this.api.post("/cart/remove-item", { index, uuid }).subscribe((response) => this.handleExternalResponse(response));
  }

  clearCart() {
    this._cart = [];
    localStorage.setItem("cart", JSON.stringify(this._cart));
    this.updateCartTimestamp();
    localStorage.removeItem("paymentIntentId");
    this.cartSubject?.next(this._cart);
    this.resetTimeout();
    return this._cart;
  }

  clearCartExternal() {
    return this.api.post("/cart/clear-cart").toPromise();
  }

  checkIfOnCheckoutPage() {
    return this.router.url === "/checkout";
  }
}
